import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { OrderWithDetails, OrderFilters } from '../types';

interface OrdersState {
  orders: OrderWithDetails[];
  filters: OrderFilters;
  selectedOrder: OrderWithDetails | null;
  isLoading: boolean;
  error: string | null;
  setOrders: (orders: OrderWithDetails[]) => void;
  setFilters: (filters: OrderFilters) => void;
  setSelectedOrder: (order: OrderWithDetails | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useOrdersStore = create<OrdersState>()(
  immer((set) => ({
    orders: [],
    filters: {},
    selectedOrder: null,
    isLoading: false,
    error: null,
    setOrders: (orders) =>
      set((state) => {
        state.orders = orders;
      }),
    setFilters: (filters) =>
      set((state) => {
        state.filters = filters;
      }),
    setSelectedOrder: (order) =>
      set((state) => {
        state.selectedOrder = order;
      }),
    setLoading: (isLoading) =>
      set((state) => {
        state.isLoading = isLoading;
      }),
    setError: (error) =>
      set((state) => {
        state.error = error;
      }),
  }))
);

// API functions
export async function fetchOrders(filters: OrderFilters = {}) {
  const store = useOrdersStore.getState();
  store.setLoading(true);
  store.setError(null);

  try {
    const queryParams = new URLSearchParams();
    if (filters.status_id) queryParams.set('status_id', filters.status_id.toString());
    if (filters.fno_id) queryParams.set('fno_id', filters.fno_id.toString());
    if (filters.search) queryParams.set('search', filters.search);

    const response = await fetch(`https://service-delivery-portal.apextech.workers.dev/api/orders?${queryParams}`);
    if (!response.ok) throw new Error('Failed to fetch orders');

    const { data } = await response.json();
    store.setOrders(data);
  } catch (error) {
    store.setError(error instanceof Error ? error.message : 'An error occurred');
  } finally {
    store.setLoading(false);
  }
}

export async function updateOrderStatus(orderId: number, statusId: number, notes?: string) {
  const store = useOrdersStore.getState();
  store.setLoading(true);
  store.setError(null);

  try {
    const response = await fetch('https://service-delivery-portal.apextech.workers.dev/api/orders', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_id: orderId, status_id: statusId, notes }),
    });

    if (!response.ok) throw new Error('Failed to update order status');

    // Refresh orders to get updated data
    await fetchOrders(store.filters);
  } catch (error) {
    store.setError(error instanceof Error ? error.message : 'An error occurred');
  } finally {
    store.setLoading(false);
  }
}

export async function completeTask(
  orderId: number,
  taskId: number,
  completedBy: string,
  notes?: string
) {
  const store = useOrdersStore.getState();
  store.setLoading(true);
  store.setError(null);

  try {
    const response = await fetch('https://service-delivery-portal.apextech.workers.dev/api/tasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order_id: orderId,
        status_task_id: taskId,
        completed_by: completedBy,
        notes,
      }),
    });

    if (!response.ok) throw new Error('Failed to complete task');

    // Refresh orders to get updated data
    await fetchOrders(store.filters);
  } catch (error) {
    store.setError(error instanceof Error ? error.message : 'An error occurred');
  } finally {
    store.setLoading(false);
  }
}
